import { React, useState } from 'react';
import {
    Box, Button, Flex, Text, Divider,
    Icon, SimpleGrid, VStack, useColorModeValue,
} from '@chakra-ui/react';
import {
    PlaneLanding, Car, TrainFront, Bus, Clock, Send, Link2, Calendar, PlusCircle, MinusCircle,
} from 'lucide-react';
import SimpleSidebar from 'views/global/SimpleSidebar';
import BoxDataTable from 'views/global/BoxDataTable';
import { QuickAlert } from 'theme/components/alerts';
import handleSidebar from 'lib/handleSidebar';
import { userController } from 'lib/user';
import BeatLoader from 'react-spinners/BeatLoader';
import { notificationStatusLabels, notificationTypeLabel, labelFor } from 'lib/labels';

const METHOD_ICONS = {
    flight: PlaneLanding,
    car:    Car,
    train:  TrainFront,
    bus:    Bus,
};

const METHOD_LABELS = {
    flight: 'Aereo',
    car:    'Auto',
    train:  'Treno',
    bus:    'Autobus',
};

function InfoRow({ label, value, labelColor }) {
    if (!value) return null;
    return (
        <Flex align="baseline" gap="8px" py="5px" borderBottom="1px solid" borderColor="gray.100">
            <Text fontSize="12px" fontWeight="600" color={labelColor} minW="140px" textTransform="uppercase" letterSpacing="0.05em">
                {label}
            </Text>
            <Text fontSize="14px" color="gray.800">{value}</Text>
        </Flex>
    );
}

function ArrivalsTab(props) {
    const { id, title } = props;

    const { setAlert, clearAlert, showAlert } = QuickAlert();
    const { getSingleRecord, saveRecord } = userController();

    const [status, setStatus] = useState({
        sidebarForm: false,
        detailLoad:  false,
    });
    const [detail, setDetail] = useState(null);
    const [reminderLoading, setReminderLoading] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notifExpanded, setNotifExpanded] = useState(false);

    const mutedColor = useColorModeValue('gray.500', 'whiteAlpha.600');
    const labelColor = useColorModeValue('gray.600', 'whiteAlpha.600');
    const rowBg      = useColorModeValue('gray.50',  'whiteAlpha.50');

    const openDetail = (row) => {
        setDetail(null);
        setNotifications([]);
        setNotifExpanded(false);
        clearAlert();
        setStatus(s => ({ ...s, sidebarForm: true, detailLoad: true }));
        handleSidebar();
        getSingleRecord(`/user/arrival/${row.id}/detail`)
            .then(res => {
                setDetail(res.data);
                setStatus(s => ({ ...s, detailLoad: false }));
                getSingleRecord('/user/reservation/notification/list?reservation_id=' + row.reservation_id)
                    .then(r => setNotifications(r.data.rows || []))
                    .catch(() => setNotifications([]));
            })
            .catch(() => {
                setAlert('error', { detail: ['Impossibile caricare i dettagli dell\'arrivo.'] });
                setStatus(s => ({ ...s, detailLoad: false }));
            });
    };

    const hideSidebar = () => {
        setStatus(s => ({ ...s, sidebarForm: false }));
        setDetail(null);
        handleSidebar(false);
        clearAlert();
    };

    const sendReminder = (id) => {
        setReminderLoading(true);
        clearAlert();
        saveRecord(`/user/arrival/${id}/reminder`, {})
            .then(response => {
                setReminderLoading(false);
                setAlert('success', response.data.message);
            })
            .catch(error => {
                setReminderLoading(false);
                setAlert('error', error.response.data.errors);
            });
    };

    const tableMenuItems = [
        { icon: null, title: 'Vedi Dettagli', handle: (row) => openDetail(row) },
    ];

    return (
        <>
            <Box>
                {!status.sidebarForm && showAlert()}

                <BoxDataTable
                    title={title}
                    src="/user/arrival/list"
                    additionalOptions={id ? { property_id: id } : {}}
                    defaultFilters={{ period: '72' }}
                    search={true}
                    checkbox={false}
                    tableMenu={tableMenuItems}
                    tableContent={(item) => (
                        <Box key={item.id} onClick={() => openDetail(item)} cursor="pointer">
                            <Text className="reservation-item-title" mb="6px">
                                <Box
                                    as="span" display="inline-block" w="9px" h="9px"
                                    borderRadius="full" me="6px" position="relative" top="0px"
                                    bg={item.status === 'completed' ? 'green.400' : 'orange.400'}
                                />
                                {item.reservation?.name ?? `Prenotazione #${item.reservation_id}`}
                            </Text>
                            <Flex gap="16px" flexWrap="wrap">
                                {item.reservation?.property && (
                                    <Text className="muted-text">{item.reservation.property}</Text>
                                )}
                                {item.reservation?.check_in && (
                                    <Text className="muted-text">
                                        {item.reservation.check_in} → {item.reservation.check_out}
                                    </Text>
                                )}
                                {item.submitted ? (
                                    <Flex align="center" gap="4px">
                                        <Icon as={Clock} boxSize="12px" color={mutedColor} />
                                        <Text className="muted-text">{item.arrival_time}</Text>
                                        {item.arrival_method && (
                                            <Text className="muted-text" textTransform="capitalize">
                                                · {METHOD_LABELS[item.arrival_method] ?? item.arrival_method}
                                            </Text>
                                        )}
                                    </Flex>
                                ) : (
                                    <Text className="muted-text">Non compilato</Text>
                                )}
                            </Flex>
                        </Box>
                    )}
                />

                {/* ── Detail sidebar ── */}
                <SimpleSidebar
                    status={status.sidebarForm}
                    title="Dettagli Arrivo"
                    cancel={{ onClick: hideSidebar, text: 'Chiudi' }}
                    viewOnly={true}
                    loading={status.detailLoad}
                    leftAction={detail && !detail.submitted ? (
                        <Button
                            variant="outline"
                            colorScheme="brand"
                            fontWeight="light"
                            leftIcon={reminderLoading ? null : <Send />}
                            _hover={{ bg: 'brand.500', color: 'white' }}
                            transition="background-color 0.2s linear"
                            isDisabled={reminderLoading}
                            onClick={() => sendReminder(detail.id)}
                        >
                            {reminderLoading
                                ? <BeatLoader loading={true} size={8} aria-label="Loading Spinner" color="#422CFA" />
                                : 'Invia Promemoria'
                            }
                        </Button>
                    ) : null}
                >
                    {status.sidebarForm && showAlert()}

                    {status.detailLoad && (
                        <Flex justify="center" py="40px">
                            <BeatLoader size={10} color="#422CFA" />
                        </Flex>
                    )}

                    {!status.detailLoad && detail && (
                        <Box>
                            {/* Reservation summary */}
                            <Box bg={rowBg} borderRadius="12px" p="16px" mb="20px">
                                <Text fontSize="11px" fontWeight="700" color={labelColor} textTransform="uppercase" letterSpacing="0.08em" mb="10px">
                                    Riepilogo Prenotazione
                                </Text>
                                {detail.reservation?.property && (
                                    <Text fontSize="18px" fontWeight="700" color="gray.800" mb="2px">
                                        {detail.reservation.property}
                                    </Text>
                                )}
                                <Text fontSize="16px" fontWeight="700" color="gray.800" mb="12px">
                                    Prenotazione #{detail.reservation?.id}
                                </Text>
                                <SimpleGrid columns={2} gap="8px" mb="8px">
                                    <Box bg="white" borderRadius="8px" px="12px" py="8px">
                                        <Text fontSize="11px" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.05em">Check-in</Text>
                                        <Text fontSize="14px" fontWeight="600">{detail.reservation?.check_in}</Text>
                                    </Box>
                                    <Box bg="white" borderRadius="8px" px="12px" py="8px">
                                        <Text fontSize="11px" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.05em">Check-out</Text>
                                        <Text fontSize="14px" fontWeight="600">{detail.reservation?.check_out}</Text>
                                    </Box>
                                </SimpleGrid>
                                {detail.arrival_url && (
                                    <Flex align="center" gap="6px" bg="white" borderRadius="8px" px="12px" py="8px">
                                        <Icon as={Link2} color="brand.500" boxSize="14px" flexShrink={0} />
                                        <Text fontSize="11px" color={mutedColor} fontWeight="600" textTransform="uppercase" letterSpacing="0.05em" flexShrink={0}>Link Arrivo</Text>
                                        <Text
                                            as="a"
                                            href={detail.arrival_url}
                                            target="_blank"
                                            fontSize="13px"
                                            color="brand.500"
                                            fontWeight="500"
                                            noOfLines={1}
                                            overflow="hidden"
                                            textOverflow="ellipsis"
                                            _hover={{ textDecoration: 'underline' }}
                                        >
                                            {detail.arrival_url}
                                        </Text>
                                    </Flex>
                                )}
                            </Box>

                            {/* Status */}
                            <Flex align="center" gap="8px" mb="20px">
                                <Box
                                    w="10px" h="10px" borderRadius="full" flexShrink={0}
                                    bg={detail.submitted ? 'green.400' : 'orange.400'}
                                />
                                <Text fontSize="14px" fontWeight="600" color={detail.submitted ? 'green.600' : 'orange.500'}>
                                    {detail.submitted ? 'Compilato' : 'Non compilato'}
                                </Text>
                                {detail.submitted_at && (
                                    <Text fontSize="12px" color={mutedColor} ml="4px">{detail.submitted_at}</Text>
                                )}
                            </Flex>

                            <Divider mb="20px" />

                            {detail.submitted ? (
                                <VStack spacing={0} align="stretch">
                                    <InfoRow label="Orario di Arrivo" value={detail.arrival_time} labelColor={labelColor} />
                                    <InfoRow
                                        label="Mezzo di Arrivo"
                                        value={detail.arrival_method
                                            ? `${METHOD_LABELS[detail.arrival_method] ?? detail.arrival_method}`
                                            : null}
                                        labelColor={labelColor}
                                    />
                                    <InfoRow label="Numero Volo / Treno" value={detail.transport_id} labelColor={labelColor} />
                                    <InfoRow label="Note" value={detail.comments} labelColor={labelColor} />
                                </VStack>
                            ) : (
                                <Text fontSize="14px" color={mutedColor}>
                                    L'ospite non ha ancora comunicato i dati di arrivo.
                                </Text>
                            )}

                            <Divider my="20px" />

                            {/* Notification history */}
                            <Box className='reservation-tasks-list' marginBottom={4}>
                                <Flex className="rtl-title" justify="space-between" align="center" cursor="pointer" onClick={() => setNotifExpanded(p => !p)}>
                                    <span>Storico Notifiche</span>
                                    {notifExpanded ? <MinusCircle /> : <PlusCircle />}
                                </Flex>
                                {notifExpanded && (
                                    notifications.length === 0 ? (
                                        <Text className="muted-text">Nessuna notifica inviata.</Text>
                                    ) : (
                                        <ul>
                                            {notifications.map((n, i) => (
                                                <li key={"notif_" + i}>
                                                    <Text className='rtl-task-title'>{notificationTypeLabel(n.type)}</Text>
                                                    <Flex className='task-meta'>
                                                        <Text className="muted-text d-flex d-icon-5 d-icon-fx-18" me={4}><Send /> {n.recipient}</Text>
                                                        <Text className={"muted-text d-flex d-icon-5 d-icon-fx-18 " + n.status} textTransform="capitalize" me={4}>{labelFor(notificationStatusLabels, n.status)}</Text>
                                                        <Text className="muted-text d-flex d-icon-5" me={4}><Calendar /> {n.sent_at}</Text>
                                                    </Flex>
                                                </li>
                                            ))}
                                        </ul>
                                    )
                                )}
                            </Box>
                        </Box>
                    )}
                </SimpleSidebar>
            </Box>
        </>
    );
}

export default ArrivalsTab;
